import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApplicationConfig, importProvidersFrom, inject, provideEnvironmentInitializer, Provider } from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import { TRANSLATE_ADAPTOR } from '@tc-angular/design-system';
import { MAPBOX_API_KEY } from 'ngx-mapbox-gl';
import { environment } from 'src/environments/environment';
import { ENVIRONMENT } from './core/injection-tokens';
import { ErrorInterceptor } from './core/interceptors/error-interceptor/error.interceptor';
import { AcceptLanguageInterceptor } from './core/interceptors/accept-language-interceptor/accept-language.interceptor';
import { JwtInterceptor } from './core/interceptors/jwt-interceptor/jwt.interceptor';
import { StageConfigService } from './core/services/stage-config/stage-config.service';
import { NgxTranslateAdaptor } from './shared/translate/ngx-translate-adaptor';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { StoreModule } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { reducers } from './core/store/reducers';
import { SharedModule } from './shared/shared.module';
import { provideRouter, withInMemoryScrolling, withNavigationErrorHandler } from '@angular/router';
import { routes } from './app.routes';
import { LAZY_CHUNK_LOAD_RECOVERY_FACADE } from './core/services/facades/lazy-chunk-load-recovery/lazy-chunk-load-recovery-facade.token';

function provideMapboxApiKey(): Provider {
  return {
    provide: MAPBOX_API_KEY,
    useFactory: (): string => {
      const stageConfigService = inject(StageConfigService);

      return stageConfigService.config.mapboxAccessToken;
    },
  };
}

export const applicationConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(
      BrowserModule,
      BrowserAnimationsModule,
      TranslateModule.forRoot(),
      SharedModule,
      StoreModule.forRoot(reducers),
    ),
    provideTranslateHttpLoader({ prefix: './assets/i18n/', suffix: '.json' }),
    { provide: HTTP_INTERCEPTORS, useClass: AcceptLanguageInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: ENVIRONMENT, useValue: environment },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { subscriptSizing: 'dynamic' } },
    { provide: TRANSLATE_ADAPTOR, useClass: NgxTranslateAdaptor },
    provideMapboxApiKey(),
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(
      routes,
      withInMemoryScrolling({
        anchorScrolling: 'disabled',
        scrollPositionRestoration: 'enabled',
      }),
      withNavigationErrorHandler((error) => {
        inject(LAZY_CHUNK_LOAD_RECOVERY_FACADE).handleNavigationError(error);
      }),
    ),
    ...environment.platformSpecificProviders,
    provideEnvironmentInitializer(() => {
      inject(LAZY_CHUNK_LOAD_RECOVERY_FACADE).start();
    }),
  ],
};
